import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import api, { apiErrorMsg } from "@/lib/api";

export default function SiswaPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // siswa being edited
  const [form, setForm] = useState({ nama: "", nosis: "" });
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/siswa").then((r) => setList(r.data)).catch((e) => toast.error(apiErrorMsg(e))).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!form.nama || !form.nosis) return toast.error("Nama & NOSIS wajib diisi");
    try {
      await api.post("/admin/siswa", form);
      toast.success("Serdik ditambahkan");
      setForm({ nama: "", nosis: "" });
      setCreating(false);
      load();
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/admin/siswa/${id}`, editing);
      toast.success("Tersimpan");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  const remove = async (id, nama) => {
    if (!window.confirm(`Hapus ${nama}?`)) return;
    try {
      await api.delete(`/admin/siswa/${id}`);
      toast.success("Dihapus");
      load();
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Master Data</div>
          <h1 className="font-heading text-3xl font-bold text-navy-900">Data Serdik</h1>
          <p className="text-slate-500 mt-1 text-sm">Daftar peserta didik yang akan dinilai.</p>
        </div>
        <button
          onClick={() => setCreating(!creating)}
          data-testid="siswa-add-btn"
          className="bg-brass-500 hover:bg-brass-600 text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Tambah Serdik
        </button>
      </div>

      {creating && (
        <form onSubmit={submitNew} className="bg-white border border-brass-300 rounded-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end animate-fade-in">
          <div className="md:col-span-1">
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Nama Lengkap</label>
            <input
              autoFocus
              value={form.nama}
              onChange={(e) => setForm({ ...form, nama: e.target.value })}
              placeholder="Letda Cpn ..."
              data-testid="siswa-input-nama"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-brass-500 focus:border-brass-500 outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">NOSIS</label>
            <input
              value={form.nosis}
              onChange={(e) => setForm({ ...form, nosis: e.target.value })}
              placeholder="2301001"
              data-testid="siswa-input-nosis"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-brass-500 focus:border-brass-500 outline-none text-sm font-mono"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="siswa-save-btn" className="bg-navy-900 hover:bg-navy-800 text-white px-4 py-2 rounded-sm text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Simpan</button>
            <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-sm border border-slate-300 text-sm hover:bg-slate-50">Batal</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">
              <th className="text-left p-3 w-12">#</th>
              <th className="text-left p-3">Nama</th>
              <th className="text-left p-3 w-40">NOSIS</th>
              <th className="text-right p-3 w-32">Aksi</th>
            </tr>
          </thead>
          <tbody data-testid="siswa-list">
            {loading ? (
              <tr><td colSpan="4" className="p-4 md:p-8 text-center text-slate-400">Memuat...</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan="4" className="p-4 md:p-8 text-center text-slate-400">Belum ada serdik. Klik &quot;Tambah Serdik&quot;.</td></tr>
            ) : (
              list.map((s, i) => (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`siswa-row-${s.id}`}>
                  <td className="p-3 text-slate-500 font-mono">{i + 1}</td>
                  <td className="p-3">
                    {editing?.id === s.id ? (
                      <input
                        value={editing.nama}
                        onChange={(e) => setEditing({ ...editing, nama: e.target.value })}
                        className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm"
                      />
                    ) : (
                      <span className="font-medium text-navy-900">{s.nama}</span>
                    )}
                  </td>
                  <td className="p-3 font-mono text-slate-600">
                    {editing?.id === s.id ? (
                      <input
                        value={editing.nosis}
                        onChange={(e) => setEditing({ ...editing, nosis: e.target.value })}
                        className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono"
                      />
                    ) : s.nosis}
                  </td>
                  <td className="p-3 text-right">
                    {editing?.id === s.id ? (
                      <div className="flex justify-end gap-1">
                        <button onClick={() => saveEdit(s.id)} className="p-1.5 text-emerald-700 hover:bg-emerald-50 rounded-sm" title="Simpan"><Save className="w-4 h-4" /></button>
                        <button onClick={() => setEditing(null)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-sm" title="Batal"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setEditing(s)} className="p-1.5 text-blue-700 hover:bg-blue-50 rounded-sm" title="Edit"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(s.id, s.nama)} className="p-1.5 text-red-700 hover:bg-red-50 rounded-sm" title="Hapus"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-slate-500">Total: <span className="font-bold text-navy-900 font-mono">{list.length}</span> serdik</div>
    </div>
  );
}
